import axios from 'axios';
import securedAxios from '../../../../../utils/secured-axios';

import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import { MerchantPurchaseConfirmData } from '../../../../../interfaces/merchants/products/purchases/confirm';
import { MerchantPurchaseCustomFieldsData } from '../../../../../interfaces/merchants/products/purchases/custom_fields';
import * as IInputs from '../../../../../interfaces/merchants/products/purchases/inputs';

import { StripeResponse } from '../../../../../utils/stripe';

import * as b from './apiBodyBuilder';
import { FirstCharge } from '../../../../../interfaces/merchants/products/firstCharge';
import { captureException } from '@sentry/nextjs';
import { buildApiV2Url } from 'src/utils/request-url';

export const fetchProduct = async (
  merchantPublicId: string,
  productPublicId: string,
) => {
  const { data } = await securedAxios.get<IPurchases.MerchantProductData>(
    buildApiV2Url(`/merchants/${merchantPublicId}/products/${productPublicId}`),
  );

  return data;
};

export const fetchCustomFields = async (productId: string) => {
  const { data } = await securedAxios.get<MerchantPurchaseCustomFieldsData>(
    buildApiV2Url(`/products/${productId}/custom_fields`),
  );
  return data;
};

export const fetchConfirmation = async (productId: string) => {
  const { data } = await securedAxios.get<MerchantPurchaseConfirmData>(
    buildApiV2Url(`/products/${productId}/rules`),
  );
  return data;
};

export const fetchMerchantProductFirstCharge = async (
  merchantPublicId: string,
  productPublicId: string,
) => {
  const { data } = await securedAxios.get<FirstCharge>(
    buildApiV2Url(
      `/merchants/${merchantPublicId}/products/${productPublicId}/first_charge`,
    ),
  );
  return data;
};

const inputsToPostStructure = (
  inputs: IInputs.State,
): IPurchases.PurchasingRequestInput => {
  let postObject = {};
  if (inputs.userInfo) {
    const userInfo = b.withUserInfo(inputs.userInfo);
    postObject = { ...postObject, ...userInfo };
  }
  if (inputs.payment) {
    const payment = b.withPayment(inputs.payment);
    postObject = { ...postObject, ...payment };
  }
  return postObject;
};

const _postPurchasing = async (
  url: string,
  reqData: IPurchases.PurchasingRequestData,
): Promise<IPurchases.PurchasingResponseData> => {
  try {
    const { data } = await securedAxios.post<
      IPurchases.PurchasingRequestData,
      { data: IPurchases.PurchasingResponseData }
    >(url, reqData);
    return data;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      if (e.response.status === 400) {
        return { ...e.response.data };
      } else {
        return { success: false };
      }
    }
    throw e;
  }
};

const purchasingUrl = (schemeType: IPurchases.SchemeType): string => {
  if (schemeType === IPurchases.SchemeType.Subscription) {
    return buildApiV2Url(`/subscriptions`);
  }
  if (schemeType === IPurchases.SchemeType.TicketBook) {
    return buildApiV2Url(`/ticket_books`);
  }
  throw new Error(`cannot create url: schemeType ${schemeType}`);
};

export const postPurchasingCustomer = async (
  schemeType: IPurchases.SchemeType,
  productId: string,
  inputs: IInputs.State,
) =>
  _postPurchasing(`${purchasingUrl(schemeType)}/validate/customer`, {
    input: await inputsToPostStructure(inputs),
    product_id: productId,
  });

export const postPurchasingPayment = async (
  schemeType: IPurchases.SchemeType,
  productId: string,
  inputs: IInputs.State,
) =>
  _postPurchasing(`${purchasingUrl(schemeType)}/validate/payment`, {
    input: await inputsToPostStructure(inputs),
    product_id: productId,
  });

export const postPurchasingFinish = async (
  schemeType: IPurchases.SchemeType,
  productId: string,
  inputs: IInputs.State,
) =>
  _postPurchasing(purchasingUrl(schemeType), {
    input: await inputsToPostStructure(inputs),
    product_id: productId,
  });

export const postStripe = async (
  _payment: IInputs.Payment,
  _jcbEnabled: boolean,
): Promise<StripeResponse | undefined> => {
  // 事前決済が発生する導線は全て新フローに移行したはずなので、このメソッドが呼ばれることはないはず
  // M5も含めて全て新フローに移行した後に全て消す
  captureException('Never');
  throw new Error('Never');
};
